import { backgroundConfig } from '../config/BackgroundConfig';
import { log, warn, error as logError } from '../utils/logger';
import { saveToStorageAsync, loadFromStorageAsync, removeFromStorageAsync } from './storage';

export interface UnsplashImage {
  id: string;
  urls: {
    raw?: string;
    regular: string;
    full: string;
    small: string;
  };
  width?: number;
  height?: number;
  user: {
    name: string;
    username: string;
  };
  description: string | null;
  alt_description: string | null;
  links: {
    html: string;
  };
}

// Sample fallback image in case the API fails
export const FALLBACK_IMAGE: UnsplashImage = {
  id: 'fallback-1',
  urls: {
    raw: 'https://images.unsplash.com/photo-1519817650390-64a93db51149',
    regular: 'https://images.unsplash.com/photo-1519817650390-64a93db51149?w=1080&q=85&fm=jpg&fit=max&cs=srgb',
    full: 'https://images.unsplash.com/photo-1519817650390-64a93db51149?w=1920&q=90&fm=jpg&fit=max&cs=srgb',
    small: 'https://images.unsplash.com/photo-1519817650390-64a93db51149?w=400&q=80&fm=jpg&fit=max&cs=srgb',
  },
  width: 2400,
  height: 1600,
  user: {
    name: 'Fallback Image',
    username: 'unsplash',
  },
  description: 'Fallback mosque image',
  alt_description: 'Mosque',
  links: {
    html: 'https://unsplash.com',
  },
};

// Local storage key for cached images
export const CACHE_KEY = 'mosque_background_cache';
export const CACHE_TIMESTAMP_KEY = 'mosque_background_timestamp';
export const LAST_LOAD_DATE_KEY = 'mosque_background_last_load_date';

/**
 * Helper to check if API key is available
 * @returns Boolean indicating if API key is set
 */
export const hasApiKey = (): boolean => {
  return !!process.env.REACT_APP_UNSPLASH_API_KEY;
};

/**
 * Fetches a random mosque photo from Unsplash API
 * @returns Promise with a single Unsplash image
 */
export const fetchMosqueImage = async (): Promise<UnsplashImage> => {
  try {
    // Get API key from environment variables
    const apiKey = process.env.REACT_APP_UNSPLASH_API_KEY;
    if (!apiKey) {
      warn('Missing Unsplash API key, using fallback image');
      return FALLBACK_IMAGE;
    }
    
    const minWidth = (backgroundConfig as any).minWidth || 1600;
    const minHeight = (backgroundConfig as any).minHeight || 900;
    const maxAttempts = (backgroundConfig as any).maxFetchAttempts || 3;

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      // Query for a single mosque photo with filters
      const response = await fetch(
        `https://api.unsplash.com/photos/random?query=${encodeURIComponent(backgroundConfig.searchQuery)}&orientation=landscape&content_filter=high&count=1`,
        {
          headers: {
            Authorization: `Client-ID ${apiKey}`
          }
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`API Error (${response.status}): ${errorText}`);
      }

      const data = await response.json() as UnsplashImage[];
      const image = data && data.length > 0 ? data[0] : null;

      if (!image) {
        warn(`Unsplash returned no image (attempt ${attempt}/${maxAttempts})`);
        continue;
      }

      // Use provided width/height metadata if available, otherwise try to infer
      const width = (image as any).width || image.width || 0;
      const height = (image as any).height || image.height || 0;

      // If width/height not provided, assume it's high quality but still verify URL
      const meetsResolution = width >= minWidth && height >= minHeight;

      if (meetsResolution) {
        log(`Selected high-resolution image ${image.id} (${width}x${height})`);
        // Ensure urls contain raw/full/regular keys — keep the object as-is
        return image;
      }

      // If metadata isn't sufficient, accept if we have a raw URL (likely high quality)
      if ((!width || !height) && (image.urls.raw || image.urls.full)) {
        log(`Accepting image ${image.id} without width/height metadata but with raw/full URL (attempt ${attempt}/${maxAttempts})`);
        return image;
      }

      warn(`Image ${image.id} did not meet resolution ${minWidth}x${minHeight} (was ${width}x${height}) — attempt ${attempt}/${maxAttempts}`);
    }

    warn('Failed to fetch a sufficiently large image from Unsplash after multiple attempts; using fallback');
    return FALLBACK_IMAGE;
  } catch (error) {
    logError('Error in unsplashApi:', error);
    // Return fallback image instead of throwing
    return FALLBACK_IMAGE;
  }
};

/**
 * Gets cached image from localStorage if available
 * @returns Cached Unsplash image or null if none found
 */
export const getCachedImage = async (): Promise<UnsplashImage | null> => {
  if (!backgroundConfig.enableCache) {
    log('Cache disabled, not retrieving cached image');
    return null;
  }

  try {
  const cachedData = await loadFromStorageAsync<string | null>(CACHE_KEY, null);
    if (!cachedData) {
      log('No cached data found in storage');
      return null;
    }

    const cachedImage = JSON.parse(cachedData) as UnsplashImage;
    if (!cachedImage || !cachedImage.id) {
      warn('Cached data is not a valid image object');
      return null;
    }

    log(`Retrieved cached image from storage: ${cachedImage.id}`);
    return cachedImage;
  } catch (error) {
    logError('Error parsing cached image:', error);
    await removeFromStorageAsync(CACHE_KEY); // Clear corrupted cache
    await removeFromStorageAsync(CACHE_TIMESTAMP_KEY);
    log('Cleared corrupted cache');
    return null;
  }
};

/**
 * Saves image to localStorage cache with timestamp
 * @param image Unsplash image to cache
 */
export const cacheImage = async (image: UnsplashImage): Promise<void> => {
  if (!backgroundConfig.enableCache || !image) {
    log('Cache not enabled or no image to cache');
    return;
  }

  try {
    const timestamp = Date.now();
    log(`Caching image at ${new Date(timestamp).toISOString()}: ${image.id}`);
    
    await saveToStorageAsync(CACHE_KEY, JSON.stringify(image));
    await saveToStorageAsync(CACHE_TIMESTAMP_KEY, timestamp.toString());
  } catch (error) {
    logError('Error caching image:', error);
    // Try to clear some space in localStorage if it's full
    try {
      await removeFromStorageAsync(CACHE_KEY);
      await removeFromStorageAsync(CACHE_TIMESTAMP_KEY);
      log('Cleared cache due to error');
    } catch (e) {
      logError('Failed to clear localStorage cache:', e);
    }
  }
};

/**
 * Checks if cached images are still valid (within the change interval)
 * @returns Boolean indicating if cache is still valid
 */
export const isCacheValid = (): boolean => {
  if (!backgroundConfig.enableCache) {
    log('Cache disabled in config');
    return false;
  }

  try {
    // read directly from localStorage for sync check
    const timestamp = localStorage.getItem(CACHE_TIMESTAMP_KEY);
    if (!timestamp) {
      log('No cache timestamp found in localStorage');
      return false;
    }

    const cacheTime = parseInt(timestamp, 10);
    if (isNaN(cacheTime)) {
      log('Invalid cache timestamp:', timestamp);
      return false;
    }

    const currentTime = Date.now();
    const timeDiff = currentTime - cacheTime;
    const isValid = timeDiff < backgroundConfig.changeInterval;

    log('Cache validation details:', {
      cacheTime: new Date(cacheTime).toISOString(),
      currentTime: new Date(currentTime).toISOString(),
      timeDiffMs: timeDiff,
      timeDiffHours: (timeDiff / (1000 * 60 * 60)).toFixed(2),
      changeIntervalMs: backgroundConfig.changeInterval,
      changeIntervalHours: (backgroundConfig.changeInterval / (1000 * 60 * 60)).toFixed(2),
      isValid,
      timeRemainingHours: ((backgroundConfig.changeInterval - timeDiff) / (1000 * 60 * 60)).toFixed(2)
    });

    return isValid;
  } catch (error) {
    logError('Error checking cache validity:', error);
    return false;
  }
};

/**
 * Returns the provided image (since we now store only one)
 * @param image Unsplash image
 * @returns The same image
 */
export const getImage = (image: UnsplashImage): UnsplashImage => {
  if (!image) {
    return FALLBACK_IMAGE;
  }
  return image;
};

/**
 * Gets image URL based on configured quality
 * @param image Unsplash image object
 * @returns URL string for the appropriate image size
 */
// High-quality raw URL parameters: 2560px wide, 90% quality, sRGB, auto format
const RAW_QUALITY_PARAMS = 'w=2560&q=90&fm=jpg&fit=max&cs=srgb';

export const getImageUrl = (image: UnsplashImage): string => {
  if (!image || !image.urls) {
    return FALLBACK_IMAGE.urls.regular;
  }

  switch (backgroundConfig.imageSize) {
    case 'raw': {
      const rawUrl = image.urls.raw || image.urls.full;
      if (!rawUrl) return image.urls.regular;
      // Append quality params — Unsplash raw URLs use imgix, which accepts these
      const separator = rawUrl.includes('?') ? '&' : '?';
      return `${rawUrl}${separator}${RAW_QUALITY_PARAMS}`;
    }
    case 'full':
      return image.urls.full;
    case 'small':
      return image.urls.small;
    case 'regular':
    default:
      return image.urls.regular;
  }
};

/**
 * Clear image cache
 * @returns Boolean indicating success
 */
export const clearImageCache = async (): Promise<boolean> => {
  try {
    await removeFromStorageAsync(CACHE_KEY);
    await removeFromStorageAsync(CACHE_TIMESTAMP_KEY);
    log('Manually cleared image cache');
    return true;
  } catch (error) {
    logError('Error clearing image cache:', error);
    return false;
  }
};

/**
 * Checks if we've already loaded the background today
 * @returns Boolean indicating if background was already loaded today
 */
export const hasLoadedToday = async (): Promise<boolean> => {
  try {
    const lastLoadDate = await loadFromStorageAsync<string | null>(LAST_LOAD_DATE_KEY, null);
    if (!lastLoadDate) {
      return false;
    }

    const today = new Date().toDateString();
    return lastLoadDate === today;
  } catch (error) {
    logError('Error checking last load date:', error);
    return false;
  }
};

/**
 * Marks that we've loaded the background today
 */
export const markLoadedToday = async (): Promise<void> => {
  try {
    const today = new Date().toDateString();
    await saveToStorageAsync(LAST_LOAD_DATE_KEY, today);
    log('Marked background as loaded today:', today);
  } catch (error) {
    logError('Error marking load date:', error);
  }
};

// Export clearImageCache to window for debugging (only in development)
if (process.env.NODE_ENV === 'development') {
  (window as any).clearImageCache = clearImageCache;
}